import React, { useRef, useEffect, useState } from "react";
import { InstagramLogo } from "@phosphor-icons/react";
import {
  animateTitle,
  animateDescription,
  animateImage,
  animateSocial,
  animateGallery,
  fadeInImage,
  cleanupScrollTriggers,
} from "./gsap";

const ChiefBarketingOfficer: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const socialRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const mainImageRef = useRef<HTMLImageElement>(null);
  const galleryImageRefs = useRef<(HTMLImageElement | null)[]>([]);

  // Image loading states following the standardized pattern
  const [imagesLoaded, setImagesLoaded] = useState<Record<string, boolean>>({});
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const pabloPhotos = [
    {
      id: "pablo-1",
      src: "/images/pablo-the-pug/pablo-the-pug-1.jpg",
      srcWebp: "/images/pablo-the-pug/pablo-the-pug-1.webp",
      alt: "Pablo the Pug smiling",
    },
    {
      id: "pablo-2",
      src: "/images/pablo-the-pug/pablo-the-pug-2.jpg",
      srcWebp: "/images/pablo-the-pug/pablo-the-pug-2.webp",
      alt: "Pablo the Pug enjoying the shade",
    },
    {
      id: "pablo-3",
      src: "/images/pablo-the-pug/pablo-the-pug-3.jpg",
      srcWebp: "/images/pablo-the-pug/pablo-the-pug-3.webp",
      alt: "Pablo the Pug lounging on a cozy blanket",
    },
    {
      id: "pablo-4",
      src: "/images/pablo-the-pug/pablo-the-pug-4.jpg",
      srcWebp: "/images/pablo-the-pug/pablo-the-pug-4.webp",
      alt: "Pablo the Pug looking serious",
    },
  ];

  // Initialize image loading states
  useEffect(() => {
    const initialLoadingState: Record<string, boolean> = {};
    const initialErrorState: Record<string, boolean> = {};

    // Initialize main image state
    initialLoadingState["pablo-main"] = false;
    initialErrorState["pablo-main"] = false;

    // Initialize gallery images states
    pabloPhotos.forEach((photo) => {
      initialLoadingState[photo.id] = false;
      initialErrorState[photo.id] = false;
    });

    setImagesLoaded(initialLoadingState);
    setImageErrors(initialErrorState);

    // Initialize gallery refs array
    galleryImageRefs.current = new Array(pabloPhotos.length).fill(null);
  }, []);

  // Image load handler following the standardized pattern
  const handleImageLoad = (
    id: string,
    index: number,
    img: HTMLImageElement
  ) => {
    console.log(`✅ Image loaded successfully for: ${id}`);
    setImagesLoaded((prev) => ({ ...prev, [id]: true }));

    // Apply GSAP fade-in animation
    fadeInImage(img, index);
  };

  // Image error handler
  const handleImageError = (id: string, src: string) => {
    console.error(`❌ Failed to load image for ${id}: ${src}`);
    setImageErrors((prev) => ({ ...prev, [id]: true }));
  };

  useEffect(() => {
    // Apply animations with a small timeout to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      if (
        sectionRef.current &&
        titleRef.current &&
        descriptionRef.current &&
        imageRef.current &&
        socialRef.current &&
        galleryRef.current
      ) {
        // Animate the elements
        animateTitle(
          { current: titleRef.current },
          { current: sectionRef.current }
        );
        animateDescription(
          { current: descriptionRef.current },
          { current: sectionRef.current }
        );
        animateImage(
          { current: imageRef.current },
          { current: sectionRef.current }
        );
        animateSocial(
          { current: socialRef.current },
          { current: sectionRef.current }
        );
        animateGallery(
          { current: galleryRef.current },
          { current: sectionRef.current }
        );
      }
    }, 100);

    // Cleanup animations and timeout on unmount
    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, []);

  return (
    <div ref={sectionRef} className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <h2
          ref={titleRef}
          className="text-3xl md:text-4xl font-bold mb-8 text-center"
        >
          Meet Pablo The Pug
        </h2>

        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div ref={imageRef} className="lg:w-1/2 order-1 lg:order-1">
            <div className="rounded-lg overflow-hidden shadow-xl relative">
              {/* Loading indicator for main image */}
              {!imagesLoaded["pablo-main"] && !imageErrors["pablo-main"] && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 min-h-[400px]">
                  <div className="w-10 h-10 border-4 border-cannabis-green-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}

              {/* Error fallback for main image */}
              {imageErrors["pablo-main"] && (
                <div className="min-h-[400px] flex items-center justify-center bg-gray-100">
                  <div className="text-center p-8">
                    <svg
                      className="w-12 h-12 text-gray-400 mb-4 mx-auto"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <p className="font-medium text-gray-700">Pablo's Image</p>
                  </div>
                </div>
              )}

              {/* Main Pablo image with optimized loading */}
              {!imageErrors["pablo-main"] && (
                <picture>
                  <source
                    srcSet="/images/pablo-the-pug/pablo-the-pug.webp"
                    type="image/webp"
                  />
                  <img
                    ref={mainImageRef}
                    src="/images/pablo-the-pug/pablo-the-pug.jpg"
                    alt="Pablo the Pug - Chief Barketing Officer at Street Lawyer Services"
                    className="w-full h-auto"
                    style={{ opacity: 0 }} // Start hidden for smooth fade-in
                    loading="lazy"
                    fetchPriority="auto"
                    decoding="async"
                    onLoad={(e) =>
                      handleImageLoad(
                        "pablo-main",
                        0,
                        e.target as HTMLImageElement
                      )
                    }
                    onError={() =>
                      handleImageError(
                        "pablo-main",
                        "/images/pablo-the-pug/pablo-the-pug.jpg"
                      )
                    }
                  />
                </picture>
              )}

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-cannabis-green-800/80 to-transparent p-4">
                <span className="text-white font-bold text-xl block text-center">
                  Chief Barketing Officer
                </span>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2 order-2 lg:order-2">
            <div ref={descriptionRef} className="mb-8">
              <h3 className="text-2xl font-semibold mb-6 text-cannabis-green-600">
                SLS Chief Barketing Officer & Director of Puplic Relations
              </h3>
              <p className="text-gray-700 mb-6 text-lg">
                Pablo the Pug is the SLS Chief Barketing Officer and Director of
                Puplic Relations. Follow Pablo the Pug on Instagram to see him
                with SLS Dispensary, customers, events and cannabis community!
              </p>
              <p className="text-gray-700 italic">
                Pablo loves greeting customers, attending cannabis events, and
                maintaining quality control by napping in the sunlight streaming
                through the dispensary windows.
              </p>
            </div>

            <div ref={socialRef}>
              <h4 className="font-bold mb-3">Follow Pablo's Adventures:</h4>
              <a
                href="https://www.instagram.com/pablotheplugg_/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex btn-primary"
                aria-label="Follow Pablo the Pug on Instagram"
              >
                <InstagramLogo size={24} weight="fill" />
                <span>@pablotheplugg_</span>
              </a>
            </div>
          </div>
        </div>

        {/* Pablo's Gallery */}
        <div ref={galleryRef} className="mt-16">
          <h3 className="text-2xl font-semibold mb-8 text-center">
            Pablo's Adventures
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {pabloPhotos.map((photo, index) => (
              <div
                key={photo.id}
                className="overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 gallery-item relative"
              >
                {/* Loading indicator for gallery images */}
                {!imagesLoaded[photo.id] && !imageErrors[photo.id] && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 aspect-square">
                    <div className="w-6 h-6 border-4 border-cannabis-green-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}

                {/* Error fallback for gallery images */}
                {imageErrors[photo.id] && (
                  <div className="aspect-square flex items-center justify-center bg-gray-100">
                    <div className="text-center p-4">
                      <svg
                        className="w-8 h-8 text-gray-400 mb-2 mx-auto"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <p className="text-xs font-medium text-gray-700">
                        Pablo #{index + 1}
                      </p>
                    </div>
                  </div>
                )}

                {/* Gallery image with optimized loading */}
                {!imageErrors[photo.id] && (
                  <picture>
                    <source srcSet={photo.srcWebp} type="image/webp" />
                    <img
                      ref={(el) => (galleryImageRefs.current[index] = el)}
                      src={photo.src}
                      alt={photo.alt}
                      className="w-full h-full object-cover aspect-square transition-transform duration-500 hover:scale-105"
                      style={{ opacity: 0 }} // Start hidden for smooth fade-in
                      loading="lazy"
                      fetchPriority="auto"
                      decoding="async"
                      onLoad={(e) =>
                        handleImageLoad(
                          photo.id,
                          index + 1,
                          e.target as HTMLImageElement
                        )
                      }
                      onError={() => handleImageError(photo.id, photo.src)}
                    />
                  </picture>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChiefBarketingOfficer;
